import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { API_BASE, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const GENERIC = "__generic__";

export default function ContactFormDialog({
  open,
  onOpenChange,
  presetCourseId,
  presetCourseTitle,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course_id: presetCourseId || GENERIC,
    message: "",
    privacy_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [catalog, setCatalog] = useState([]);
  // Allegato facoltativo: il modulo di iscrizione compilato (PDF).
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, course_id: presetCourseId || GENERIC }));
    setDone(false);
    setFile(null);
    // Public list, no auth needed
    axios
      .get(`${API_BASE}/public/catalog`)
      .then((r) => setCatalog(r.data || []))
      .catch(() => setCatalog([]));
  }, [open, presetCourseId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }
    setSubmitting(true);
    try {
      // multipart per poter allegare il modulo di iscrizione (PDF, facoltativo).
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      if (form.phone.trim()) fd.append("phone", form.phone.trim());
      if (form.course_id !== GENERIC) fd.append("course_id", form.course_id);
      fd.append("message", form.message.trim());
      fd.append("privacy_accepted", "true");
      if (file) fd.append("file", file);
      await axios.post(`${API_BASE}/public/contact`, fd);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    // reset for next open
    setTimeout(() => {
      setForm({
        name: "",
        email: "",
        phone: "",
        course_id: presetCourseId || GENERIC,
        message: "",
        privacy_accepted: false,
      });
      setDone(false);
      setFile(null);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(v) : close())}>
      <DialogContent
        data-testid="contact-form-dialog"
        className="max-w-xl rounded-sm bg-white"
      >
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">
            Contatta l'amministrazione
          </DialogTitle>
          <DialogDescription>
            Compila il modulo e ti ricontatteremo il prima possibile. Nessun impegno.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div
            data-testid="contact-form-success"
            className="text-center py-8"
          >
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-sm bg-emerald-50 text-emerald-600 mb-4">
              <CheckCircle2 size={28} strokeWidth={1.75} />
            </div>
            <h3 className="text-xl text-brand-blue mb-2">Richiesta inviata</h3>
            <p className="text-brand-muted max-w-sm mx-auto text-sm">
              Sarai ricontattato al più presto all'indirizzo email indicato. Grazie
              per l'interesse in Buro Academy.
            </p>
            <button
              onClick={close}
              className="btn-primary mt-6 inline-flex items-center gap-2"
              data-testid="contact-form-close"
            >
              Chiudi
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Nome e cognome *</Label>
                <Input
                  data-testid="contact-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  minLength={2}
                  className="mt-1.5 h-10 rounded-sm"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  data-testid="contact-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="mt-1.5 h-10 rounded-sm"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Telefono</Label>
                <Input
                  data-testid="contact-form-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 h-10 rounded-sm"
                  placeholder="+39 000 000 0000"
                />
              </div>
              <div>
                <Label>Corso di interesse</Label>
                <Select
                  value={form.course_id}
                  onValueChange={(v) => setForm({ ...form, course_id: v })}
                >
                  <SelectTrigger
                    data-testid="contact-form-course"
                    className="mt-1.5 h-10 rounded-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GENERIC}>Richiesta generica</SelectItem>
                    {catalog.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {presetCourseTitle && form.course_id !== GENERIC && (
              <div className="text-xs text-brand-muted">
                Interesse preselezionato: <strong>{presetCourseTitle}</strong>
              </div>
            )}
            <div>
              <Label>Messaggio *</Label>
              <textarea
                data-testid="contact-form-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                minLength={5}
                rows={4}
                className="mt-1.5 w-full rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Scrivi la tua richiesta o le domande sul corso…"
              />
            </div>
            <div>
              <Label>
                Allega il modulo di iscrizione compilato{" "}
                <span className="font-normal text-brand-muted">(facoltativo, PDF)</span>
              </Label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                data-testid="contact-form-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1.5 block w-full text-sm text-brand-muted file:mr-3 file:rounded-sm file:border-0 file:bg-brand-blue/10 file:text-brand-blue file:font-semibold file:px-3 file:py-2 file:cursor-pointer hover:file:bg-brand-blue/20 cursor-pointer"
              />
              {file && (
                <div
                  className="mt-1.5 flex items-center gap-2 text-xs text-brand-muted"
                  data-testid="contact-form-file-selected"
                >
                  <span className="truncate">
                    {file.name} · {Math.round(file.size / 1024)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="text-brand-blue hover:underline shrink-0"
                  >
                    rimuovi
                  </button>
                </div>
              )}
            </div>
            <label
              className="flex items-start gap-2 text-sm text-brand-muted cursor-pointer select-none"
              data-testid="contact-form-privacy-label"
            >
              <input
                data-testid="contact-form-privacy"
                type="checkbox"
                checked={form.privacy_accepted}
                onChange={(e) =>
                  setForm({ ...form, privacy_accepted: e.target.checked })
                }
                className="mt-1 h-4 w-4 accent-brand-blue"
                required
              />
              <span>
                Ho letto e accetto il trattamento dei dati per essere ricontattato *
                {" — "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="contact-form-privacy-link"
                  className="text-brand-blue underline hover:no-underline"
                >
                  leggi l'informativa
                </a>
              </span>
            </label>
            {!form.privacy_accepted && (
              <div
                data-testid="contact-form-privacy-hint"
                className="text-xs text-amber-700 -mt-2"
              >
                Accetta l'informativa per abilitare l'invio.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={submitting || !form.privacy_accepted}
                data-testid="contact-form-submit"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Invia
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
